'use client'

import { memo } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'

/** Floating pixel sparks. CRT static that drifts */
const PIXEL_SPARKS = [
  { x: '8%', y: '18%', size: 3, delay: 0, duration: 7.2 },
  { x: '22%', y: '62%', size: 2, delay: 1.1, duration: 8.4 },
  { x: '71%', y: '24%', size: 4, delay: 0.4, duration: 6.8 },
  { x: '84%', y: '58%', size: 2, delay: 2.2, duration: 9.1 },
  { x: '46%', y: '78%', size: 3, delay: 0.8, duration: 7.6 },
  { x: '14%', y: '42%', size: 2, delay: 1.6, duration: 8.8 },
  { x: '92%', y: '36%', size: 3, delay: 0.2, duration: 6.4 },
  { x: '58%', y: '12%', size: 2, delay: 2.8, duration: 9.6 },
] as const

/** CRT scanlines, vignette, floating pixels, and ambient scan for 8-bit landing */
export const Ninxl8BitAtmosphere = memo(function Ninxl8BitAtmosphere() {
  const shouldReduce = useReducedMotion()
  const { scrollYProgress } = useScroll()
  const scanOpacity = useTransform(scrollYProgress, [0, 0.5], [0.4, 0.12])
  const sparkOpacity = useTransform(scrollYProgress, [0, 0.35, 0.7], [0.55, 0.35, 0.12])

  return (
    <div className="ninxl-8bit-atmosphere pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
      <div className="ninxl-theme-atmosphere-bg absolute inset-0" />

      {/* Subtle pixel-dot texture behind content */}
      <div className="ninxl-8bit-atmosphere-dots absolute inset-0 opacity-30" />

      {/* Soft teal bloom. CSS pauses under prefers-reduced-motion */}
      <div className="ninxl-8bit-atmosphere-bloom absolute inset-0" />

      {/* CRT scanline overlay */}
      <div className="ninxl-crt-overlay absolute inset-0" />

      {/* Occasional scan sweep */}
      {!shouldReduce ? (
        <motion.div
          className="ninxl-8bit-scan-sweep absolute inset-x-0 h-28 bg-gradient-to-b from-transparent via-[#21728d]/8 to-transparent"
          style={{ opacity: scanOpacity }}
        />
      ) : null}

      {/* Floating pixel sparks */}
      <motion.div
        className="absolute inset-0"
        style={{ opacity: shouldReduce ? 0.2 : sparkOpacity }}
      >
        {PIXEL_SPARKS.map((spark) => (
          <span
            key={`${spark.x}-${spark.y}`}
            className="ninxl-8bit-pixel-spark absolute"
            style={{
              left: spark.x,
              top: spark.y,
              width: spark.size,
              height: spark.size,
              animationDelay: `${spark.delay}s`,
              animationDuration: `${spark.duration}s`,
            }}
          />
        ))}
      </motion.div>

      <div className="ninxl-crt-vignette absolute inset-0" />
    </div>
  )
})
